
import { useState, useCallback, useEffect } from 'react';
import { supabaseService } from '../supabaseService';

export type LikeResourceType = 'post' | 'book' | 'chapter';

export const useLike = (
  resourceId: string,
  resourceType: LikeResourceType,
  initialLiked: boolean = false,
  initialCount: number = 0
) => {
  const [liked, setLiked] = useState(initialLiked);
  const [likesCount, setLikesCount] = useState(initialCount);
  const [isAnimating, setIsAnimating] = useState(false);

  // Preserve highest count to prevent likes count from dropping to zero
  useEffect(() => {
    if (initialCount > 0) {
      setLikesCount((prev) => Math.max(prev, initialCount));
    }
  }, [initialCount]);

  useEffect(() => {
    let isMounted = true;
    const fetchLikedStatus = async () => {
      if (!resourceId) return;
      try {
        const isUserLiked = await supabaseService.isLiked(resourceId, resourceType);
        if (isMounted) setLiked(isUserLiked);
        
        // Fetch like count without wiping existing higher count
        const count = await supabaseService.getLikeCount(resourceId, resourceType);
        if (isMounted && count !== null && count > 0) {
          setLikesCount((prev) => Math.max(prev, count));
        }
      } catch (e) {
        console.error("Failed to fetch like status", e);
      }
    };
    fetchLikedStatus();
    return () => { isMounted = false; };
  }, [resourceId, resourceType]);

  const toggleLike = useCallback(async () => {
    // Require account to like
    try {
      const user = await supabaseService.getCurrentUser();
      if (!user) {
        window.dispatchEvent(new CustomEvent('auth-required', { detail: { action: 'like' } }));
        return;
      }
    } catch {
      window.dispatchEvent(new CustomEvent('auth-required', { detail: { action: 'like' } }));
      return;
    }

    const prevLiked = liked;
    const newLiked = !prevLiked;

    // Optimistic update
    setLiked(newLiked);
    setLikesCount(prev => (newLiked ? prev + 1 : Math.max(0, prev - 1)));
    
    if (newLiked) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 1000);
    }

    try {
      await supabaseService.toggleLike(resourceId, 'me', newLiked, resourceType);
    } catch (err) {
      console.error("Failed to toggle like:", err);
      // Revert on error
      setLiked(prevLiked);
      setLikesCount(prev => (!newLiked ? prev + 1 : Math.max(0, prev - 1)));
    }
  }, [resourceId, resourceType, liked]);

  const forceLike = useCallback(async () => {
    if (liked) return;

    // Require account to like
    try {
      const user = await supabaseService.getCurrentUser();
      if (!user) {
        window.dispatchEvent(new CustomEvent('auth-required', { detail: { action: 'like' } }));
        return;
      }
    } catch {
      window.dispatchEvent(new CustomEvent('auth-required', { detail: { action: 'like' } }));
      return;
    }
    
    setLiked(true);
    setLikesCount(prev => prev + 1);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 1000);

    try {
      await supabaseService.toggleLike(resourceId, 'me', true, resourceType);
    } catch (err) {
      console.error("Failed to force like:", err);
      setLiked(false);
      setLikesCount(prev => Math.max(0, prev - 1));
    }
  }, [resourceId, resourceType, liked]);

  return {
    liked,
    setLiked,
    likesCount,
    setLikesCount,
    isAnimating,
    toggleLike,
    forceLike
  };
};
